import { parseStoredSession, type OnboardingSessionState } from "./schema";

const STORAGE_KEY_PREFIX = "staylayer:onboarding:editor:v2:";
const DISABLED_KEY = "staylayer:onboarding:editor:disabled";

function storageKeyFor(userId: string) {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

/**
 * Read the persisted session for this user. Returns null on miss, on parse
 * failure, on storage unavailability, and on corrupted shapes. Callers must
 * treat null as "start fresh".
 */
export function loadSession(userId: string): OnboardingSessionState | null {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) return null;
    return parseStoredSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveSession(state: OnboardingSessionState): void {
  try {
    localStorage.setItem(storageKeyFor(state.userId), JSON.stringify(state));
  } catch {
    // Storage unavailable (quota, private mode, etc). Session continues in
    // memory; we just won't survive refresh. Logged by the provider.
  }
}

export function clearSession(userId: string): void {
  try {
    localStorage.removeItem(storageKeyFor(userId));
  } catch {
    // ignore
  }
}

/**
 * Per-user kill switch for staff debugging. When set, the provider mounts
 * nothing regardless of the feature flag.
 */
export function isMentorDisabledLocally(): boolean {
  try {
    return localStorage.getItem(DISABLED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Debounced writer. The provider calls this on every transition; we coalesce
 * to avoid storage thrash during step churn.
 */
export function createDebouncedSaver(delayMs = 250) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: OnboardingSessionState | null = null;

  const flush = () => {
    if (pending) {
      saveSession(pending);
      pending = null;
    }
    timer = null;
  };

  return {
    save(state: OnboardingSessionState) {
      pending = state;
      if (timer) return;
      timer = setTimeout(flush, delayMs);
    },
    flushNow() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      flush();
    },
  };
}
